import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import dayjs, { Dayjs } from "dayjs";
import {
    getClassificationLabel,
    Imagery,
    ModelClassification,
    ImageryType,
    isWaterImageryType,
    ImageryDetails,
} from "@/types/imagery";
import classNames from "classnames";
import {
    Button,
    DatePicker,
    Divider,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Select,
    SelectItem,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Slider,
    NumberInput,
    Checkbox,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faThumbsUp,
    faThumbsDown,
    faStar,
    faClose,
    faCaretRight,
    faCaretLeft,
    faMap,
    faSlash,
    faHeart,
    faEllipsisVertical,
    faDownload,
    faLayerGroup,
    faEyeLowVision,
    faRectangleList,
    faDrawPolygon,
    faPrescription,
    faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import utc from "dayjs/plugin/utc";
import NStatusChip from "../common/NStatusChip";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import { useTranslation } from "react-i18next";
import { Resource } from "@/types/user";
import {
    DateValue,
    getLocalTimeZone,
    parseDate,
} from "@internationalized/date";
import { MoistureStatus } from "@/types/water";
import { QueryObserverResult } from "@tanstack/react-query";
import { RequestContextType } from "@/lib/request/RequestProvider";
import {
    addUpdateGeojsonSource,
    moveLayersToTop,
    updateLayerOpacity,
    removeLayersIfExists,
    removeSourcesIfExists,
    addUpdateGeojsonLayers,
    invertedTurfBboxToFourCornerCoordinates,
    Coordinates,
} from "@/lib/utils/map";
import { IMAGERY_LAYER } from "@/lib/map/layers";
import useMap from "@/lib/map/useMap";
import { FeatureCollection, MultiPolygon } from "geojson";
import {
    FillLayerSpecification,
    ImageSource,
    LineLayerSpecification,
    SymbolLayerSpecification,
} from "mapbox-gl";
import { applyMZZoneNumberColors, MZ_COLORS } from "@/lib/map/layers";
import { area as turfArea } from "@turf/turf";
import { Rx } from "@/types/rx";
import { RxType } from "@/types/enums";
import { useNavigate } from "@tanstack/react-router";

dayjs.extend(utc);

type MutateRequest = ReturnType<RequestContextType["useMutateRequest"]>;

const CARD_SIZE = 44; // px — width and height of each square card
const CARD_GAP = 8; // px — corresponds to gap-2 in Tailwind

const USABLE_OVERLAY_SOURCE = "usable-overlay-source";

function DayImagesTable({
    images,
    selectedImage,
    setSelectedImage,
    onClose,
    onScore,
    onPrefer,
    favoriteImagery,
    downloadImagery,
    downloadImageryLoading,
    dayLabel,
    fieldImageryQuery,
    fieldIsTrial,
    triggerPartialAnalysis,
    partialAnalysis,
    showUsableBoundary,
    setShowUsableBoundary,
}: {
    images: Imagery[];
    selectedImage?: Imagery;
    setSelectedImage?: (image?: Imagery) => void;
    onClose: () => void;
    onScore?: (imagery: Imagery, classification: ModelClassification) => void;
    onPrefer?: (imagery: Imagery, manually_preferred: boolean) => void;
    favoriteImagery: MutateRequest;
    downloadImagery: () => void;
    downloadImageryLoading: boolean;
    dayLabel: string;
    fieldImageryQuery?: QueryObserverResult<Imagery[]>;
    fieldIsTrial: boolean;
    triggerPartialAnalysis: (imageryId?: number) => void;
    partialAnalysis: MutateRequest;
    showUsableBoundary: boolean;
    setShowUsableBoundary: (show: boolean) => void;
}) {
    const { isAtLeastDetail, userIsSentinel } = useAuth();
    const showQuality = isAtLeastDetail(Resource.IMAGERY_TIMELINE);

    const columns = useMemo(() => {
        const base = [
            { key: "capture_date", label: "Capture date" },
            { key: "time", label: "Time" },
        ];
        if (!fieldIsTrial) base.push({ key: "n_status", label: "N Status" });
        base.push({ key: "source", label: "Source" });
        base.push({ key: "quality", label: "Quality" });
        base.push({ key: "actions", label: "Actions" });
        return base;
    }, [fieldIsTrial]);

    const sortedImages = useMemo(() => {
        return [...(images ?? [])].sort((a, b) =>
            dayjs(b.capture_date).diff(dayjs(a.capture_date)),
        );
    }, [images]);

    const tableItems = useMemo(
        () =>
            sortedImages.map((image) => ({
                ...image,
                showUsableBoundary,
            })),
        [sortedImages, showUsableBoundary],
    );

    const renderCell = useCallback(
        (item: Imagery, columnKey: string) => {
            switch (columnKey) {
                case "capture_date":
                    return dayjs
                        .utc(item.capture_date)
                        .local()
                        .format("YYYY-MM-DD");
                case "time":
                    return dayjs
                        .utc(item.capture_date)
                        .local()
                        .format("h:mm a");
                case "n_status":
                    return item.n_status ? (
                        <NStatusChip nStatus={item.n_status} />
                    ) : (
                        "--"
                    );
                case "source":
                    return item.imagery_subscription?.provider_name ?? "--";
                case "quality":
                    return (
                        <span className="flex flex-col gap-0.5">
                            {item.usable ? (
                                <span className="flex items-center gap-1 text-green-500">
                                    Good <FontAwesomeIcon icon={faCheck} />
                                </span>
                            ) : (
                                <span className="flex items-center gap-1 text-red-500">
                                    Bad <FontAwesomeIcon icon={faThumbsDown} />
                                </span>
                            )}
                            {userIsSentinel && (
                                <span>
                                    Model:{" "}
                                    {getClassificationLabel(
                                        item.model_classification,
                                    )}
                                </span>
                            )}
                        </span>
                    );
                case "actions":
                    return (
                        <Popover placement="top">
                            <PopoverTrigger>
                                <Button isIconOnly size="sm" variant="light">
                                    <FontAwesomeIcon
                                        icon={faEllipsisVertical}
                                    />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent>
                                <div className="flex flex-col gap-1">
                                    {showQuality && (
                                        <>
                                            <Button
                                                size="sm"
                                                color="success"
                                                aria-label="Good"
                                                onPress={() =>
                                                    onScore?.(
                                                        item,
                                                        ModelClassification.good,
                                                    )
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faThumbsUp}
                                                    className="text-white"
                                                />
                                                Good
                                            </Button>
                                            <Button
                                                size="sm"
                                                color="danger"
                                                aria-label="Bad"
                                                onPress={() =>
                                                    onScore?.(
                                                        item,
                                                        ModelClassification.bad,
                                                    )
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faThumbsDown}
                                                />
                                                Bad
                                            </Button>
                                            <Button
                                                size="sm"
                                                isDisabled={!item.usable}
                                                variant="light"
                                                aria-label="Preferred"
                                                onPress={() =>
                                                    onPrefer?.(
                                                        item,
                                                        !item.active_for_day,
                                                    )
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faStar}
                                                    color={
                                                        item.active_for_day
                                                            ? "#FFD700"
                                                            : undefined
                                                    }
                                                />
                                                Primary
                                            </Button>
                                        </>
                                    )}
                                    <Button
                                        size="sm"
                                        variant={
                                            item.favorite ? "solid" : "light"
                                        }
                                        aria-label="View"
                                        isLoading={
                                            favoriteImagery.isPending ||
                                            fieldImageryQuery?.isFetching
                                        }
                                        onPress={() => {
                                            favoriteImagery.mutate(
                                                {
                                                    url: `imagery/${item.id}/`,
                                                    method: "put",
                                                    data: {
                                                        favorite:
                                                            !item.favorite,
                                                    },
                                                },
                                                {
                                                    onSuccess: () => {
                                                        fieldImageryQuery?.refetch();
                                                    },
                                                },
                                            );
                                        }}
                                        color={
                                            item.favorite ? "danger" : "default"
                                        }
                                    >
                                        <FontAwesomeIcon icon={faHeart} />
                                        Favorite
                                    </Button>
                                    {userIsSentinel && (
                                        <div className="flex flex-col gap-1 rounded-md border border-default-200 bg-default-50 p-2 pt-0">
                                            <Button
                                                size="sm"
                                                variant="light"
                                                color={
                                                    item.use_partial_analysis
                                                        ? "secondary"
                                                        : "default"
                                                }
                                                aria-label="Partial Analysis"
                                                onPress={() => {
                                                    triggerPartialAnalysis(
                                                        item.id,
                                                    );
                                                }}
                                                isLoading={
                                                    partialAnalysis.isPending
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faWandMagicSparkles}
                                                />
                                                Partial Analysis
                                            </Button>
                                            <Checkbox
                                                size="sm"
                                                className="text-xs"
                                                classNames={{
                                                    label: "text-xs",
                                                }}
                                                isSelected={showUsableBoundary}
                                                onValueChange={(value) => {
                                                    setShowUsableBoundary(
                                                        value,
                                                    );
                                                }}
                                            >
                                                Show Usable Boundary
                                            </Checkbox>
                                        </div>
                                    )}

                                    <Button
                                        size="sm"
                                        variant="light"
                                        aria-label="Download"
                                        onPress={downloadImagery}
                                        isLoading={downloadImageryLoading}
                                    >
                                        <FontAwesomeIcon icon={faDownload} />
                                        Download
                                    </Button>
                                </div>
                            </PopoverContent>
                        </Popover>
                    );
                case "preferred":
                    return (
                        <div className="flex gap-1">
                            <Button
                                size="sm"
                                isIconOnly
                                isDisabled={!item.usable}
                                variant="light"
                                aria-label="Preferred"
                                onPress={() =>
                                    onPrefer?.(item, !item.active_for_day)
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faStar}
                                    color={
                                        item.active_for_day
                                            ? "#FFD700"
                                            : undefined
                                    }
                                />
                            </Button>
                        </div>
                    );
                default:
                    return null;
            }
        },
        [
            showQuality,
            favoriteImagery,
            fieldImageryQuery,
            userIsSentinel,
            partialAnalysis.isPending,
            showUsableBoundary,
            setShowUsableBoundary,
            downloadImagery,
            downloadImageryLoading,
            onScore,
            onPrefer,
            triggerPartialAnalysis,
        ],
    );

    return (
        <div className="flex flex-col h-[12rem] overflow-scroll w-full ">
            <div className="flex flex-row justify-between items-center gap-2 px-2 shrink-0">
                <span className="font-semibold text-sm">{dayLabel}</span>
                <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={onClose}
                    aria-label="Close"
                >
                    <FontAwesomeIcon icon={faClose} />
                </Button>
            </div>
            <Divider />
            <div className="overflow-auto pb-24 w-full flex-1 p-1">
                <Table
                    radius="none"
                    aria-label="Images for day"
                    selectionMode="single"
                    removeWrapper
                    selectedKeys={
                        selectedImage
                            ? new Set([String(selectedImage.id)])
                            : new Set()
                    }
                    onSelectionChange={(keys) => {
                        const id = [...keys][0];
                        if (id == null) {
                            setSelectedImage?.(undefined);
                        } else {
                            const found = sortedImages.find(
                                (im) => String(im.id) === String(id),
                            );
                            if (found) setSelectedImage?.(found);
                        }
                    }}
                    color="primary"
                    classNames={{
                        td: "px-2 rounded-none",
                        tr: "cursor-pointer rounded-none first:rounded-none last:rounded-none",
                        th: "rounded-none first:rounded-none last:rounded-none",
                    }}
                >
                    <TableHeader columns={columns} className="bg-pink-500">
                        {(col) => (
                            <TableColumn
                                className="text-xs p-0 rounded-none "
                                key={col.key}
                            >
                                {col.label}
                            </TableColumn>
                        )}
                    </TableHeader>
                    <TableBody
                        items={tableItems}
                        emptyContent="No images"
                        loadingContent={<Spinner label="Loading..." />}
                    >
                        {(item) => (
                            <TableRow key={item.id} className="rounded-none">
                                {(columnKey) => (
                                    <TableCell className="text-xs py-1">
                                        {renderCell(item, columnKey as string)}
                                    </TableCell>
                                )}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div>
                <Divider />
            </div>
        </div>
    );
}

const imageZonesSource = "create-image-zones";
const fillLayer: FillLayerSpecification = {
    source: imageZonesSource,
    id: "create-zones-fill",
    type: "fill",
    layout: {
        visibility: "visible",
    },
    paint: {
        "fill-color": ["get", "fill-color"],
        "fill-opacity": 0.5,
    },
};
const lineLayer: LineLayerSpecification = {
    source: imageZonesSource,
    id: "create-zones-line",
    type: "line",
    layout: {
        visibility: "visible",
    },
    paint: {
        "line-color": ["get", "fill-color"],
        "line-width": 2,
    },
};
const labelLayer: SymbolLayerSpecification = {
    source: imageZonesSource,
    id: "create-zones-label",
    type: "symbol",
    layout: {
        visibility: "visible",
        "text-field": ["to-string", ["get", "display-value"]],
        "text-size": 14,
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-allow-overlap": true,
    },
    paint: {
        "text-color": "#ffffff",
        "text-halo-color": "#000000",
        "text-halo-width": 1.5,
    },
};
const vegetativeIndexOptions = [
    { key: ImageryType.NDRE, label: "NDRE" },
    { key: ImageryType.NDVI, label: "NDVI" },
    { key: ImageryType.SAVI, label: "SAVI" },
    { key: ImageryType.OSAVI, label: "OSAVI" },
    { key: ImageryType.CCCI, label: "CCCI" },
];
function CreateZonesFromImagery({
    image,
    selectedImageType,
    setSelectedImageType,
    onClose,
}: {
    image: Imagery;
    selectedImageType: ImageryType;
    setSelectedImageType: (imageType: ImageryType) => void;
    onClose: () => void;
}) {
    const navigate = useNavigate();
    const { map, mapLoaded } = useMap();
    const [imageType, setImageType] = useState<ImageryType>(
        vegetativeIndexOptions.map((opt) => opt.key).includes(selectedImageType)
            ? selectedImageType
            : vegetativeIndexOptions[0].key,
    );
    const [numberOfZones, setNumberOfZones] = useState(3);
    const { useMutateRequest } = useRequest();
    const createZones = useMutateRequest<
        FeatureCollection<
            MultiPolygon,
            {
                zone_number: number;
                min: number;
                max: number;
                mean: number;
                std: number;
                p25: number;
                p75: number;
            }
        >
    >({
        method: "get",
        url: `imagery/${image.id}/zones/`,
        errorMessage: "Failed to create zones",
    });
    const createRx = useMutateRequest<Rx>({
        method: "post",
        url: "/prescriptions/",
        errorMessage: "Failed to create Rx",
    });
    useEffect(() => {
        if (!map || !mapLoaded) return;
        addUpdateGeojsonSource(map, imageZonesSource, {
            type: "geojson",
            data: applyMZZoneNumberColors(
                createZones.data ?? {
                    type: "FeatureCollection",
                    features: [],
                },
            ),
        });
        addUpdateGeojsonLayers(map, [fillLayer, lineLayer, labelLayer]);
        return () => {
            if (map) {
                removeLayersIfExists(map, [
                    fillLayer.id,
                    lineLayer.id,
                    labelLayer.id,
                ]);
                removeSourcesIfExists(map, [imageZonesSource]);
            }
        };
    }, [map, mapLoaded, createZones.data]);
    useEffect(() => {
        createZones.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [image, imageType, numberOfZones]);

    return (
        <div className="flex flex-col h-auto sm:h-[12rem] overflow-scroll w-full ">
            <div className="flex flex-row justify-between items-center gap-2 px-2 shrink-0">
                <span className="font-semibold text-sm">
                    Create zones from imagery
                </span>
                <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={onClose}
                    aria-label="Close"
                >
                    <FontAwesomeIcon icon={faClose} />
                </Button>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-2 px-2 w-full">
                <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
                    <span className="text-sm font-medium leading-tight shrink-0">
                        Imagery band
                    </span>
                    <Select
                        size="sm"
                        className="flex-1 sm:w-[8rem]"
                        selectedKeys={
                            vegetativeIndexOptions &&
                                vegetativeIndexOptions.length > 0 &&
                                imageType
                                ? [imageType]
                                : []
                        }
                        onChange={(e) => {
                            setImageType?.(e.target.value as ImageryType);
                            setSelectedImageType?.(
                                e.target.value as ImageryType,
                            );
                        }}
                        classNames={{
                            base: "sm:min-w-[12rem]",
                        }}
                        aria-label="Select Imagery Type"
                    >
                        {(vegetativeIndexOptions ?? []).map((opt) => (
                            <SelectItem key={opt.key}>{opt.label}</SelectItem>
                        ))}
                    </Select>
                </div>
                <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
                    <span className="text-sm font-medium leading-tight shrink-0">
                        Number of zones
                    </span>
                    <NumberInput
                        value={numberOfZones}
                        onChange={(value) => setNumberOfZones(value as number)}
                        minValue={2}
                        maxValue={7}
                        size="sm"
                        className="flex-1 sm:w-[8rem]"
                        aria-label="Number of zones"
                        classNames={{ inputWrapper: "h-8 min-h-8" }}
                    />
                </div>
                <Button
                    size="sm"
                    aria-label="Create zones"
                    color="primary"
                    className="w-full sm:w-[8rem]"
                    isLoading={createZones.isPending}
                    isDisabled={
                        numberOfZones < 2 ||
                        numberOfZones > 7 ||
                        !image.id ||
                        !imageType
                    }
                    onPress={() => {
                        createZones.mutate({
                            params: {
                                num_zones: numberOfZones,
                                imagery_type: imageType,
                            },
                        });
                    }}
                >
                    <FontAwesomeIcon icon={faDrawPolygon} />
                    Create zones
                </Button>
            </div>
            {createZones.data &&
                createZones.data.features.length > 0 &&
                (() => {
                    const features = [...createZones.data!.features].sort(
                        (a, b) =>
                            a.properties.zone_number - b.properties.zone_number,
                    );

                    const zoneAreas = features.map((f) => turfArea(f));
                    const totalArea = zoneAreas.reduce((s, a) => s + a, 0);
                    const grandMean =
                        features.reduce(
                            (s, f, i) => s + zoneAreas[i] * f.properties.mean,
                            0,
                        ) / (totalArea || 1);
                    const withinSS = features.reduce(
                        (s, f, i) => s + zoneAreas[i] * f.properties.std ** 2,
                        0,
                    );
                    const betweenSS = features.reduce(
                        (s, f, i) =>
                            s +
                            zoneAreas[i] * (f.properties.mean - grandMean) ** 2,
                        0,
                    );
                    const r2 = betweenSS / (betweenSS + withinSS || 1);

                    return (
                        <div className="flex flex-col gap-1 px-2 py-1 overflow-auto scrollbar-hide">
                            <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-default-500 font-medium">
                                        Zone distribution
                                    </span>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-16 h-1.5 bg-default-100 rounded overflow-hidden">
                                            <div
                                                className="h-full rounded"
                                                style={{
                                                    width: `${r2 * 100}%`,
                                                    backgroundColor:
                                                        r2 >= 0.7
                                                            ? "#22c55e"
                                                            : r2 >= 0.4
                                                                ? "#eab308"
                                                                : "#ef4444",
                                                }}
                                            />
                                        </div>
                                        <span className="text-xs tabular-nums text-default-400">
                                            R² {(r2 * 100).toFixed(1)}%
                                        </span>
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    aria-label="Create Rx"
                                    color="primary"
                                    isLoading={createRx.isPending}
                                    className="w-[8rem]"
                                    onPress={() => {
                                        createRx.mutate(
                                            {
                                                data: {
                                                    field: image
                                                        .imagery_subscription
                                                        .field,
                                                    rx_type: RxType.ZONE,
                                                    template_id: image.id,
                                                    template_type: "imagery",
                                                    name: `${dayjs(image.capture_date).format("MM/DD/YYYY")} ${imageType}`,
                                                    imagery_type:
                                                        imageType ===
                                                            ImageryType.NDRE
                                                            ? "NDRE_ANALYTIC"
                                                            : imageType,
                                                    zone_size: numberOfZones,
                                                },
                                            },
                                            {
                                                onSuccess: (newRx) => {
                                                    if (newRx) {
                                                        navigate({
                                                            to: `/dashboard/fields/${image.imagery_subscription.field}/rx/${newRx.id}`,
                                                        });
                                                        onClose();
                                                    }
                                                },
                                            },
                                        );
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPrescription} />
                                    Create Rx
                                </Button>
                            </div>
                            {(() => {
                                const sortedByMean = [...features].sort(
                                    (a, b) =>
                                        a.properties.mean - b.properties.mean,
                                );
                                const globalMin = Math.min(
                                    ...features.map((f) => f.properties.min),
                                );
                                const globalMax = Math.max(
                                    ...features.map((f) => f.properties.max),
                                );
                                const range = globalMax - globalMin || 1;

                                return sortedByMean.map((f) => {
                                    const { zone_number, mean, min, max } =
                                        f.properties;
                                    const color =
                                        MZ_COLORS[
                                        zone_number % MZ_COLORS.length
                                        ];
                                    const leftPct =
                                        ((min - globalMin) / range) * 100;
                                    const widthPct = Math.max(
                                        ((max - min) / range) * 100,
                                        1,
                                    );
                                    const meanPct =
                                        ((mean - globalMin) / range) * 100;
                                    const acres = (
                                        turfArea(f) * 0.000247105
                                    ).toFixed(1);
                                    return (
                                        <div
                                            key={zone_number}
                                            className="flex items-center gap-2 text-xs min-w-0"
                                        >
                                            <div
                                                className="w-3 h-3 rounded-sm shrink-0"
                                                style={{
                                                    backgroundColor: color,
                                                }}
                                            />
                                            <span className="w-5 shrink-0 text-right font-medium">
                                                {zone_number}
                                            </span>
                                            <div className="relative flex-1 h-4 bg-default-100 rounded overflow-hidden">
                                                <div
                                                    className="absolute top-0 h-full rounded opacity-50"
                                                    style={{
                                                        left: `${leftPct}%`,
                                                        width: `${widthPct}%`,
                                                        backgroundColor: color,
                                                    }}
                                                />
                                                <div
                                                    className="absolute top-0 h-full w-0.5 opacity-90"
                                                    style={{
                                                        left: `${meanPct}%`,
                                                        backgroundColor: color,
                                                    }}
                                                />
                                            </div>
                                            <span className="w-12 shrink-0 text-right tabular-nums text-default-500">
                                                {mean.toFixed(3)}
                                            </span>
                                            <span className="w-14 shrink-0 text-right tabular-nums text-default-400">
                                                {acres} ac
                                            </span>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    );
                })()}
            <Divider className="mt-auto" />
        </div>
    );
}

export default function ImageryTimelineV2() {
    const { t } = useTranslation("messages");
    const {
        filteredImagery,
        fieldImageryQuery,
        selectedCropCycle,
        setSelectedImage,
        selectedImage,
        imageType,
        setImageType,
        imageryOptions,
        fieldWaterQuery,
        selectedDate,
        setSelectedDate,
        imageryVisible,
        setImageryVisible,
        fieldIsTrial,
        imageryBbox,
    } = useFieldDashboard() ?? {};
    const { useMutateRequest, downloadFile, useGetRequest } = useRequest();
    const [containerWidth, setContainerWidth] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const { map, mapLoaded } = useMap();
    const { userIsSentinel, isAtLeastDetail } = useAuth();

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => {
            setContainerWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const numberOfDays =
        containerWidth > 0
            ? Math.max(
                1,
                Math.floor(
                    (containerWidth + CARD_GAP) / (CARD_SIZE + CARD_GAP),
                ),
            )
            : 0;

    const [timelineStartDate, setTimelineStartDate] = useState<Dayjs>(() =>
        dayjs().subtract(4, "day"),
    );
    const timelineStartDateRef = useRef<Dayjs>(timelineStartDate);
    const [expandedDay, setExpandedDay] = useState<string | null>(null);
    const [createZonesFromImageryOpen, setCreateZonesFromImageryOpen] =
        useState(false);

    useEffect(() => {
        timelineStartDateRef.current = timelineStartDate;
    }, [timelineStartDate]);

    const imagesInCycle = useMemo(() => {
        const data = fieldImageryQuery?.data;
        if (!data) return [];
        const cycleEnd = selectedCropCycle?.end_date
            ? dayjs(selectedCropCycle.end_date).isBefore(
                getTodayWithTimeTravel(),
            )
                ? dayjs(selectedCropCycle.end_date)
                : getTodayWithTimeTravel()
            : getTodayWithTimeTravel();
        return data.filter((image) => {
            const captureDate = dayjs(image.capture_date);
            if (!selectedCropCycle) return true;
            return (
                captureDate.isAfter(
                    dayjs(selectedCropCycle.start_date).subtract(1, "day"),
                ) && captureDate.isBefore(cycleEnd.add(1, "day"))
            );
        });
    }, [fieldImageryQuery?.data, selectedCropCycle]);

    const waterEstimates = useMemo(() => {
        if (!fieldWaterQuery?.data) return [];
        const cycleEnd = selectedCropCycle?.end_date
            ? dayjs(selectedCropCycle.end_date).isBefore(
                getTodayWithTimeTravel(),
            )
                ? dayjs(selectedCropCycle.end_date)
                : getTodayWithTimeTravel()
            : getTodayWithTimeTravel();
        return fieldWaterQuery.data.filter((water) => {
            const date = dayjs(water.date);
            return (
                date.isAfter(selectedCropCycle?.start_date) &&
                date.isBefore(cycleEnd)
            );
        });
    }, [fieldWaterQuery?.data, selectedCropCycle]);

    const centerOffset = Math.floor(numberOfDays / 2);

    useEffect(() => {
        if (!imagesInCycle.length) {
            setSelectedImage?.(undefined);
            return;
        }
        let activeImages = imagesInCycle.filter((im) => im.active_for_day);
        if (!activeImages.length) {
            activeImages = imagesInCycle;
        }
        const selectedImageInCycle = imagesInCycle.some(
            (image) => image.id === selectedImage?.id,
        );
        const mostRecent = activeImages.reduce((prev, cur) =>
            new Date(cur.capture_date) > new Date(prev.capture_date)
                ? cur
                : prev,
        );
        if (mostRecent && !selectedImageInCycle) {
            setSelectedImage?.(mostRecent);
            setTimelineStartDate(
                dayjs(mostRecent.capture_date).subtract(centerOffset, "day"),
            );
            setSelectedDate?.(
                dayjs(mostRecent.capture_date).format("YYYY-MM-DD"),
            );
        }
    }, [
        imagesInCycle,
        setSelectedImage,
        centerOffset,
        setSelectedDate,
        selectedImage,
    ]);

    const timelineDays = useMemo(() => {
        if (numberOfDays === 0) return [];
        return Array(numberOfDays)
            .fill(0)
            .map((_, i) => timelineStartDate.add(i, "day"));
    }, [timelineStartDate, numberOfDays]);

    useEffect(() => {
        if (!selectedDate || numberOfDays === 0) return;

        const selected = dayjs(selectedDate).startOf("day");
        const start = timelineStartDateRef.current.startOf("day");
        const end = start.add(numberOfDays - 1, "day");

        if (selected.isBefore(start) || selected.isAfter(end)) {
            setTimelineStartDate(selected.subtract(centerOffset, "day"));
        }
    }, [selectedDate, centerOffset, numberOfDays]);

    const allImagesByDate = useMemo(() => {
        return (
            fieldImageryQuery?.data?.reduce<Record<string, Imagery[]>>(
                (acc, im) => {
                    const key = dayjs
                        .utc(im.capture_date)
                        .local()
                        .format("YYYY-MM-DD");
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(im);
                    return acc;
                },
                {},
            ) ?? {}
        );
    }, [fieldImageryQuery?.data]);

    const imagesByDate = useMemo(() => {
        return (
            filteredImagery?.reduce<Record<string, Imagery>>((acc, im) => {
                acc[dayjs.utc(im.capture_date).local().format("YYYY-MM-DD")] =
                    im;
                return acc;
            }, {}) ?? {}
        );
    }, [filteredImagery]);

    const updateImagery = useMutateRequest({
        successMessage: t("success.imagery_updated"),
        errorMessage: t("error.failed_to_update_imagery"),
    });

    const onScore = useCallback(
        (
            imagery: Imagery,
            classification: ModelClassification,
            onAfterSuccess?: () => void,
        ) => {
            updateImagery.mutate(
                {
                    url: `imagery/${imagery.id}/score/`,
                    data: { user_classification: classification },
                    method: "put",
                },
                {
                    onSuccess: () => {
                        fieldImageryQuery?.refetch();
                        onAfterSuccess?.();
                    },
                },
            );
        },
        [fieldImageryQuery, updateImagery],
    );

    const onPrefer = useCallback(
        (imagery: Imagery, manually_preferred: boolean) => {
            updateImagery.mutate(
                {
                    url: `imagery/${imagery.id}/manually_preferred/`,
                    data: { manually_preferred },
                    method: "put",
                },
                { onSuccess: () => fieldImageryQuery?.refetch() },
            );
        },
        [fieldImageryQuery, updateImagery],
    );

    const expandedDayImages = useMemo(
        () => (expandedDay ? (allImagesByDate[expandedDay] ?? []) : []),
        [expandedDay, allImagesByDate],
    );
    const expandedDayLabel = useMemo(
        () =>
            expandedDay ? dayjs(expandedDay).format("ddd, MMM D, YYYY") : "",
        [expandedDay],
    );

    const selectedDateKey = useMemo(
        () => (selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : null),
        [selectedDate],
    );

    const favoriteImagery = useMutateRequest({
        method: "put",
    });

    const [downloadImageryLoading, setDownloadImageryLoading] = useState(false);
    const downloadImagery = useCallback(async () => {
        setDownloadImageryLoading(true);
        await downloadFile(
            `imagery/${selectedImage?.id}/download/`,
            `${selectedImage?.capture_date} Image.zip`,
        ).finally(() => {
            setDownloadImageryLoading(false);
            setIsMenuOpen(false);
        });
    }, [downloadFile, selectedImage?.id, selectedImage?.capture_date]);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [opacity, setOpacity] = useState(0.8);
    const handleOpacityChange = (opacity: number | number[]) => {
        setOpacity(opacity as number);
        if (!map || !mapLoaded) return;
        updateLayerOpacity(map, [IMAGERY_LAYER], opacity as number);
    };

    const partialAnalysis = useMutateRequest({
        method: "post",
        successMessage: "Partial analysis started",
    });

    const triggerPartialAnalysis = useCallback(
        (imageryId?: number) => {
            if (!imageryId) return;
            partialAnalysis.mutate(
                {
                    url: `imagery/${imageryId}/create_usable_imagery_boundary/`,
                },
                {
                    onSuccess: () => {
                        fieldImageryQuery?.refetch();
                    },
                },
            );
        },
        [fieldImageryQuery, partialAnalysis],
    );

    const [showUsableBoundary, setShowUsableBoundary] = useState(false);

    const imageryDetails = useGetRequest<ImageryDetails>({
        url: `imagery/${selectedImage?.id}/`,
        enabled: !!selectedImage,
    });

    const bbox = useMemo(() => {
        if (imageryDetails.data?.bounds?.length) {
            return invertedTurfBboxToFourCornerCoordinates(
                imageryDetails.data?.bounds,
            ) as Coordinates;
        } else {
            return imageryBbox;
        }
    }, [imageryDetails.data?.bounds, imageryBbox]);

    useEffect(() => {
        if (
            !imageryDetails.data ||
            !mapLoaded ||
            !map ||
            !imageryDetails.data.display_urls?.usable_overlay ||
            !bbox
        ) {
            return;
        }
        if (showUsableBoundary && imageryDetails.data?.use_partial_analysis) {
            const im = map.getSource(USABLE_OVERLAY_SOURCE) as ImageSource;
            if (im) {
                if (
                    im.url !== imageryDetails.data.display_urls.usable_overlay
                ) {
                    im.updateImage({
                        url: imageryDetails.data.display_urls.usable_overlay,
                        coordinates: bbox,
                    });
                }
            } else {
                map.addSource(USABLE_OVERLAY_SOURCE, {
                    type: "image",
                    url: imageryDetails.data.display_urls.usable_overlay,
                    coordinates: bbox,
                });
                map.addLayer({
                    id: USABLE_OVERLAY_SOURCE,
                    type: "raster",
                    source: USABLE_OVERLAY_SOURCE,
                    paint: {
                        "raster-fade-duration": 0,
                    },
                });
            }
        } else {
            removeLayersIfExists(map, [USABLE_OVERLAY_SOURCE]);
            removeSourcesIfExists(map, [USABLE_OVERLAY_SOURCE]);
        }
    }, [bbox, imageryDetails.data, map, mapLoaded, showUsableBoundary]);

    return (
        <div className="flex flex-col gap-2 w-full relative">
            {expandedDay && (
                <DayImagesTable
                    images={expandedDayImages}
                    dayLabel={expandedDayLabel}
                    selectedImage={selectedImage}
                    setSelectedImage={setSelectedImage}
                    onClose={() => setExpandedDay(null)}
                    onScore={onScore}
                    onPrefer={onPrefer}
                    favoriteImagery={favoriteImagery}
                    downloadImagery={downloadImagery}
                    downloadImageryLoading={downloadImageryLoading}
                    fieldImageryQuery={fieldImageryQuery}
                    fieldIsTrial={fieldIsTrial ?? false}
                    triggerPartialAnalysis={triggerPartialAnalysis}
                    partialAnalysis={partialAnalysis}
                    showUsableBoundary={showUsableBoundary}
                    setShowUsableBoundary={setShowUsableBoundary}
                />
            )}
            {createZonesFromImageryOpen && selectedImage && (
                <CreateZonesFromImagery
                    image={selectedImage}
                    selectedImageType={
                        imageType ?? vegetativeIndexOptions[0].key
                    }
                    setSelectedImageType={setImageType ?? (() => { })}
                    onClose={() => setCreateZonesFromImageryOpen(false)}
                />
            )}
            <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-2 flex-wrap px-2 pt-2">
                <div className="flex flex-row items-center gap-2">
                    <DatePicker
                        aria-label="Select Date"
                        showMonthAndYearPickers
                        size="sm"
                        className="w-[9rem]"
                        value={parseDate(
                            dayjs(selectedDate).format("YYYY-MM-DD"),
                        )}
                        onChange={(e) => {
                            const d = dayjs(e?.toDate(getLocalTimeZone()));
                            setTimelineStartDate(
                                d.subtract(centerOffset, "day"),
                            );
                            setSelectedDate?.(d.format("YYYY-MM-DD"));
                            const imOnDate =
                                imagesByDate[d.format("YYYY-MM-DD")];
                            if (imOnDate) setSelectedImage?.(imOnDate);
                        }}
                        isDateUnavailable={(date: DateValue) =>
                            isWaterImageryType(imageType)
                                ? false
                                : !waterEstimates.find(
                                    (water) =>
                                        water.date ===
                                        dayjs(
                                            date.toDate(getLocalTimeZone()),
                                        ).format("YYYY-MM-DD"),
                                ) &&
                                !imagesByDate[
                                dayjs(
                                    date.toDate(getLocalTimeZone()),
                                ).format("YYYY-MM-DD")
                                ]
                        }
                    />

                    <Button
                        size="sm"
                        color="primary"
                        variant="flat"
                        onPress={() =>
                            setExpandedDay(expandedDay ? null : selectedDateKey)
                        }
                        startContent={
                            <FontAwesomeIcon icon={faRectangleList} />
                        }
                    >
                        View Images
                    </Button>
                </div>
                <div className="flex flex-row items-center gap-2">
                    <Button
                        isIconOnly
                        size="sm"
                        variant="flat"
                        onPress={() => setImageryVisible?.((v) => !v)}
                        aria-label="Toggle imagery visibility"
                    >
                        <span className="relative inline-flex items-center justify-center">
                            <FontAwesomeIcon
                                icon={faMap}
                                className={
                                    imageryVisible
                                        ? "text-primary"
                                        : "text-default-400"
                                }
                            />
                            {!imageryVisible && (
                                <FontAwesomeIcon
                                    icon={faSlash}
                                    className="absolute text-default-400 scale-110"
                                />
                            )}
                        </span>
                    </Button>
                    <Select
                        size="sm"
                        className="w-[8rem]"
                        selectedKeys={
                            imageryOptions &&
                                imageryOptions.length > 0 &&
                                imageType
                                ? [imageType]
                                : []
                        }
                        onChange={(e) =>
                            setImageType?.(e.target.value as ImageryType)
                        }
                        classNames={{
                            base: "min-w-[8rem] md:min-w-[12rem]",
                        }}
                        aria-label="Select Imagery Type"
                    >
                        {(imageryOptions ?? []).map((opt) => (
                            <SelectItem key={opt.key}>{opt.label}</SelectItem>
                        ))}
                    </Select>
                    <Popover
                        placement="top"
                        isOpen={isMenuOpen}
                        onOpenChange={setIsMenuOpen}
                        classNames={{
                            content: "md:min-w-[12rem]",
                        }}
                    >
                        <PopoverTrigger className="z-0">
                            <Button isIconOnly size="sm" variant="flat">
                                <FontAwesomeIcon icon={faEllipsisVertical} />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent>
                            <div className="flex flex-col gap-1">
                                <Button
                                    size="sm"
                                    color="primary"
                                    variant={expandedDay ? "solid" : "light"}
                                    aria-label="View image details"
                                    onPress={() => {
                                        setExpandedDay(
                                            expandedDay
                                                ? null
                                                : selectedDateKey,
                                        );
                                        setIsMenuOpen(false);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faRectangleList} />
                                    View All Images
                                </Button>
                                {isAtLeastDetail(Resource.IMAGERY_TIMELINE) && (
                                    <>
                                        <Button
                                            size="sm"
                                            color="success"
                                            aria-label="Good"
                                            variant={
                                                selectedImage?.usable
                                                    ? "solid"
                                                    : "light"
                                            }
                                            isLoading={updateImagery.isPending}
                                            onPress={() => {
                                                if (!selectedImage) return;
                                                onScore(
                                                    selectedImage,
                                                    ModelClassification.good,
                                                    () => setIsMenuOpen(false),
                                                );
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faThumbsUp}
                                            />
                                            Good
                                        </Button>
                                        <Button
                                            size="sm"
                                            color="danger"
                                            aria-label="Bad"
                                            variant={
                                                !selectedImage?.usable
                                                    ? "solid"
                                                    : "light"
                                            }
                                            isLoading={updateImagery.isPending}
                                            onPress={() => {
                                                if (!selectedImage) return;
                                                onScore(
                                                    selectedImage,
                                                    ModelClassification.bad,
                                                    () => setIsMenuOpen(false),
                                                );
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faThumbsDown}
                                            />
                                            Bad
                                        </Button>
                                    </>
                                )}
                                <Button
                                    size="sm"
                                    variant={
                                        selectedImage?.favorite
                                            ? "solid"
                                            : "light"
                                    }
                                    aria-label="View"
                                    isLoading={favoriteImagery.isPending}
                                    onPress={() => {
                                        favoriteImagery.mutate(
                                            {
                                                url: `imagery/${selectedImage?.id}/`,
                                                method: "put",
                                                data: {
                                                    favorite:
                                                        !selectedImage?.favorite,
                                                },
                                            },
                                            {
                                                onSuccess: () => {
                                                    fieldImageryQuery?.refetch();
                                                    setIsMenuOpen(false);
                                                },
                                            },
                                        );
                                    }}
                                    color={
                                        selectedImage?.favorite
                                            ? "danger"
                                            : "default"
                                    }
                                >
                                    <FontAwesomeIcon icon={faHeart} />
                                    {selectedImage?.favorite
                                        ? "Unfavorite"
                                        : "Favorite"}
                                </Button>
                                {userIsSentinel && (
                                    <div className="flex flex-col gap-1 rounded-md border border-default-200 bg-default-50 p-2 pt-0">
                                        <Button
                                            size="sm"
                                            variant="light"
                                            color={
                                                selectedImage?.use_partial_analysis
                                                    ? "secondary"
                                                    : "default"
                                            }
                                            aria-label="Partial Analysis"
                                            onPress={() => {
                                                triggerPartialAnalysis(
                                                    selectedImage?.id,
                                                );
                                            }}
                                            isLoading={
                                                partialAnalysis.isPending
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={faWandMagicSparkles}
                                            />
                                            Partial Analysis
                                        </Button>
                                        <Checkbox
                                            size="sm"
                                            isDisabled={
                                                !selectedImage?.use_partial_analysis
                                            }
                                            isReadOnly={
                                                !selectedImage?.use_partial_analysis
                                            }
                                            className="text-xs"
                                            classNames={{
                                                label: "text-xs",
                                            }}
                                            isSelected={showUsableBoundary}
                                            onValueChange={
                                                setShowUsableBoundary
                                            }
                                        >
                                            Show Usable Boundary
                                        </Checkbox>
                                    </div>
                                )}
                                <Button
                                    size="sm"
                                    variant="light"
                                    aria-label="Download"
                                    onPress={downloadImagery}
                                    isLoading={downloadImageryLoading}
                                    color="primary"
                                >
                                    <FontAwesomeIcon icon={faDownload} />
                                    Download
                                </Button>
                                <Button
                                    size="sm"
                                    variant="light"
                                    aria-label="Bring to top"
                                    onPress={() => {
                                        if (!selectedImage || !map) return;
                                        moveLayersToTop(map, [IMAGERY_LAYER]);
                                    }}
                                    color="primary"
                                    isDisabled={
                                        !map || !mapLoaded || !imageryVisible
                                    }
                                >
                                    <FontAwesomeIcon icon={faLayerGroup} />
                                    Bring to top
                                </Button>
                                <Slider
                                    size="sm"
                                    aria-label="Opacity"
                                    minValue={0}
                                    maxValue={1}
                                    step={0.01}
                                    value={opacity}
                                    onChange={handleOpacityChange}
                                    className="p-1"
                                    isDisabled={
                                        !map || !mapLoaded || !imageryVisible
                                    }
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faEyeLowVision}
                                            className="text-primary"
                                        />
                                    }
                                />
                                <Button
                                    size="sm"
                                    variant="light"
                                    aria-label="Create zones"
                                    color="primary"
                                    onPress={() => {
                                        setCreateZonesFromImageryOpen(true);
                                        setIsMenuOpen(false);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faDrawPolygon} />
                                    Create zones
                                </Button>
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>
            <div className="overflow-x-auto w-full min-w-0 px-2 pb-2">
                <div className="flex flex-row items-center gap-3 w-full">
                    <div className="flex flex-row items-center gap-1 shrink-0">
                        <Button
                            isIconOnly
                            size="sm"
                            variant="flat"
                            color="primary"
                            aria-label="Previous 7 days"
                            onPress={() =>
                                setTimelineStartDate((d) =>
                                    d.subtract(numberOfDays, "day"),
                                )
                            }
                        >
                            <FontAwesomeIcon
                                icon={faCaretLeft}
                                className="text-xs"
                            />
                        </Button>
                    </div>
                    <div
                        ref={containerRef}
                        className="flex flex-1 flex-row gap-2 py-0.5 sm:p-1 min-w-0 overflow-hidden justify-center"
                    >
                        {timelineDays.map((day) => {
                            const dateKey = day.format("YYYY-MM-DD");
                            const imageOnDate = imagesByDate[dateKey];
                            const usable = imageOnDate?.usable;
                            const nStatus = fieldIsTrial
                                ? undefined
                                : imageOnDate?.n_status;
                            const waterEstimate = fieldIsTrial
                                ? undefined
                                : isWaterImageryType(imageType)
                                    ? waterEstimates.find(
                                        (water) => water.date === dateKey,
                                    )
                                    : undefined;
                            const moistureStatus = isWaterImageryType(imageType)
                                ? waterEstimate?.moisture_status
                                : undefined;
                            const selected = selectedDate === dateKey;

                            return (
                                <div
                                    key={dateKey}
                                    className="flex flex-col items-center shrink-0"
                                >
                                    <button
                                        type="button"
                                        style={{
                                            width: CARD_SIZE,
                                            height: CARD_SIZE,
                                        }}
                                        onClick={() => {
                                            if (imageOnDate)
                                                setSelectedImage?.(imageOnDate);
                                            if (expandedDay)
                                                setExpandedDay(dateKey);
                                            setSelectedDate?.(dateKey);
                                        }}
                                        className={classNames(
                                            "rounded border-2 flex flex-col items-center justify-center transition-transform ",
                                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-primary",
                                            {
                                                "ring-2 ring-primary ring-offset-1":
                                                    selected,
                                                "border-red-500 text-red-600 bg-red-100":
                                                    (usable &&
                                                        nStatus === "red" &&
                                                        !waterEstimate) ||
                                                    moistureStatus ===
                                                    MoistureStatus.APPROCHING_DEFICIENCY,
                                                "border-green-500 text-green-600 bg-green-100":
                                                    (usable &&
                                                        nStatus === "green" &&
                                                        !waterEstimate) ||
                                                    moistureStatus ===
                                                    MoistureStatus.OPTIMAL,
                                                "border-yellow-500 text-yellow-600 bg-yellow-100":
                                                    usable &&
                                                    nStatus === "yellow" &&
                                                    !waterEstimate,
                                                "border-default-500 text-default-800 bg-default-200":
                                                    imageOnDate &&
                                                    (!usable || !nStatus) &&
                                                    !moistureStatus,
                                                "border-blue-400 text-blue-600 bg-blue-100":
                                                    moistureStatus ===
                                                    MoistureStatus.APPROACHING_SATURATION,
                                                "border-default-200 text-default-400 bg-default-50 cursor-not-allowed ":
                                                    !imageOnDate &&
                                                    !waterEstimate,
                                                "cursor-pointer hover:scale-105":
                                                    imageOnDate ||
                                                    waterEstimate,
                                            },
                                        )}
                                        disabled={
                                            !imageOnDate && !waterEstimate
                                        }
                                    >
                                        <span className="text-sm font-medium leading-tight">
                                            {day.format("MMM")}
                                        </span>
                                        <span className="text-xs font-semibold leading-tight">
                                            {day.format("DD")}
                                        </span>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex flex-row items-center gap-1 shrink-0">
                        <Button
                            isIconOnly
                            size="sm"
                            variant="flat"
                            color="primary"
                            aria-label="Next 7 days"
                            onPress={() =>
                                setTimelineStartDate((d) =>
                                    d.add(numberOfDays, "day"),
                                )
                            }
                        >
                            <FontAwesomeIcon
                                icon={faCaretRight}
                                className="text-xs"
                            />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
